import api from './api'
import type {
  CompareResult,
  DealSearchSchema,
  Facets,
  QueryResult,
  SavedSearch,
  SearchFilters,
} from '@/pages/db-search/types'

/** Response payloads are used verbatim (see db-search/types.ts) — no camel-case
 *  transform, because the column keys ARE the API's field names. */

export type QueryArgs = {
  filters: SearchFilters
  columns: string[]
  sortField: string
  sortDir: 'asc' | 'desc'
  page: number
  pageSize: number
  groupBy?: string | null
}

export type ComparePeriodArgs = {
  label: string
  dateFrom?: string | null
  dateTo?: string | null
  datePreset?: string | null
}

export type ExportOutcome = {
  blob: Blob
  filename: string
  rows: number
  matched: number
  truncated: boolean
}

function filenameFrom(disposition: string | undefined, fallback: string): string {
  const match = /filename="?([^"]+)"?/.exec(disposition ?? '')
  return match?.[1] ?? fallback
}

export const dbSearchService = {
  getSchema: () => api.get<DealSearchSchema>('/db-search/schema').then((r) => r.data),

  getFacets: () => api.get<Facets>('/db-search/facets').then((r) => r.data),

  query: (args: QueryArgs, signal?: AbortSignal) =>
    api
      .post<QueryResult>('/db-search/query', args, { signal })
      .then((r) => r.data),

  aggregate: (args: Omit<QueryArgs, 'page' | 'pageSize'> & { groupBy: string }, signal?: AbortSignal) =>
    api
      .post<{ totals: QueryResult['totals']; groups: NonNullable<QueryResult['groups']> }>(
        '/db-search/aggregate',
        { ...args, page: 1, pageSize: 1 },
        { signal },
      )
      .then((r) => r.data),

  compare: (
    args: {
      filters: SearchFilters
      periods: ComparePeriodArgs[]
      dateField: string
      groupBy?: string | null
    },
    signal?: AbortSignal,
  ) => api.post<CompareResult>('/db-search/compare', args, { signal }).then((r) => r.data),

  /** One call, one file. The response carries the row counts in headers so the
   *  UI can state exactly what was written — including a truncation warning. */
  exportXlsx: async (args: {
    filters: SearchFilters
    columns: string[]
    sortField: string
    sortDir: 'asc' | 'desc'
    title?: string
  }): Promise<ExportOutcome> => {
    const response = await api.post('/db-search/export', args, { responseType: 'blob' })
    return {
      blob: response.data as Blob,
      filename: filenameFrom(response.headers['content-disposition'], 'deal-locator.xlsx'),
      rows: Number(response.headers['x-export-rows'] ?? 0),
      matched: Number(response.headers['x-export-matched'] ?? 0),
      truncated: String(response.headers['x-export-truncated']) === 'true',
    }
  },

  listViews: () => api.get<SavedSearch[]>('/db-search/views').then((r) => r.data),

  createView: (body: {
    name: string
    description?: string
    filters: SearchFilters
    columns: string[]
    sortField: string
    sortDir: 'asc' | 'desc'
    isShared?: boolean
  }) => api.post<SavedSearch>('/db-search/views', body).then((r) => r.data),

  updateView: (
    id: string,
    body: Partial<{
      name: string
      description: string
      filters: SearchFilters
      columns: string[]
      sortField: string
      sortDir: 'asc' | 'desc'
      isShared: boolean
    }>,
  ) => api.put<SavedSearch>(`/db-search/views/${id}`, body).then((r) => r.data),

  deleteView: (id: string) => api.delete(`/db-search/views/${id}`).then(() => undefined),

  /** Re-run a stored definition, optionally against a new window. */
  runView: (
    id: string,
    body: {
      datePreset?: string | null
      dateField?: string | null
      dateFrom?: string | null
      dateTo?: string | null
      page?: number
      pageSize?: number
      groupBy?: string | null
    } = {},
  ) => api.post<QueryResult>(`/db-search/views/${id}/run`, body).then((r) => r.data),
}

/** Hand a generated file to the browser without leaking the object URL. */
export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}
